import { logger } from "$lib/utils/logger";
import { reportError } from "$lib/errorHandling/errorReporter";
import { createFetchWithRetry } from "$lib/utils/fetchHelpers";
import { isPreviewMode } from "$lib/utils/previewMode";
import { recordTelemetryEvent } from "$lib/telemetry/telemetry";

interface DimensionsPayload {
  content_area_width: number;
  content_area_height: number;
}

interface PreviewPayload {
  preview: true;
}

type Payload = DimensionsPayload | PreviewPayload;

// "found" and "error" both keep content on screen; only an explicit "not_found"
// (404) means the display is gone and we should stop showing content.
type HandshakeStatus = "found" | "not_found" | "error";

interface HandshakeResult {
  status: HandshakeStatus;
}

const RELOAD_DELAY_MS = 30 * 60 * 1000; // 30 minutes

// Network errors are retried automatically by fetch-retry whenever retryOn is
// an array; 5xx is retried via the explicit list below. Up to 8 retries with
// exponential backoff. See createFetchWithRetry's JSDoc for details.
const fetch = createFetchWithRetry({
  logContext: "Handshake",
  retryOnHttpStatusCodes: [500, 502, 503, 504],
  maxRetriesCount: 8,
});

async function elev8Handshake(
  displayId: string,
  width?: number,
  height?: number
): Promise<HandshakeResult> {
  if (!displayId) {
    logger.warn("Display ID is not defined, skipping handshake");
    return { status: "error" };
  }

  // Preview iframes have iframe-relative dimensions — sending them would
  // overwrite the real screen's last-reported size on central.
  const payload: Payload = isPreviewMode()
    ? { preview: true }
    : handshakePayload(
        width ?? window.innerWidth,
        height ?? window.innerHeight
      );

  const startedAt = performance.now();
  let status: HandshakeStatus;
  try {
    const response = await sendHandshakeData(displayId, payload);
    if (response.status === 404) {
      scheduleReload(displayId, "Display not found (404)");
      status = "not_found";
    } else {
      status = "found";
    }
  } catch (error) {
    reportError("[Handshake] Failed to send handshake", error);
    scheduleReload(displayId, "Handshake failed after retries");
    status = "error";
  }
  recordTelemetryEvent({
    name: "frontend_handshake_ms",
    metricType: "histogram",
    value: performance.now() - startedAt,
    tags: { status },
    omitDisplayId: true,
  });
  return { status };
}

function scheduleReload(displayId: string, reason: string): void {
  logger.warn(
    `[Handshake] ${reason} (display: ${displayId}). Reloading in ${RELOAD_DELAY_MS / 60_000} minutes.`
  );
  setTimeout(() => location.reload(), RELOAD_DELAY_MS);
}

function handshakePayload(
  content_area_width: number,
  content_area_height: number
): DimensionsPayload {
  return {
    content_area_width,
    content_area_height,
  };
}

async function sendHandshakeData(
  displayId: string,
  payload: Payload
): Promise<Response> {
  const baseUrl = import.meta.env.VITE_ELEV8_API_BASE_URL;
  if (!baseUrl) throw new Error("Undefined env: VITE_ELEV8_API_BASE_URL");
  const url = `${baseUrl}/api/screens/${displayId}`;
  logger.info("[Handshake] Sending handshake..", payload);
  return fetch(url, {
    method: "PUT",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export type { HandshakeResult, HandshakeStatus };
export { elev8Handshake };
